import { useEffect, useState, useRef } from "react";
import { CircleNotch, ArrowClockwise, Scales, Lightning, ArrowsClockwise, Clock, Target, X, ArrowsIn, ArrowsOut, ClockCounterClockwise, Check, ArrowRight } from "@phosphor-icons/react";
import { ThinkingTimer } from "@/components/ThinkingTimer";
import { toast } from "@/components/ui/sonner";
import api from "@/lib/api";

const T = {
  trade_off: { label: "Trade-off", Icon: Scales, color: "#b45309" },
  value_conflict: { label: "Value conflict", Icon: Lightning, color: "#be123c" },
  means_end: { label: "Means-end", Icon: ArrowsClockwise, color: "#6d28d9" },
  sequencing: { label: "Sequencing", Icon: Clock, color: "#0369a1" },
};
const CAT_COLORS = ["#1d4ed8", "#b45309", "#047857", "#be123c", "#7c3aed"];
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const TensionBar = ({ className = "" }) => (
  <svg width="28" height="10" viewBox="0 0 28 10" className={`mx-1.5 inline-block shrink-0 align-middle ${className}`} aria-hidden="true">
    <line x1="2" y1="1.5" x2="2" y2="8.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <line x1="2" y1="5" x2="26" y2="5" stroke="currentColor" strokeWidth="1.3" />
    <line x1="26" y1="1.5" x2="26" y2="8.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const Building = () => (
  <div data-testid="areas-building" className="grid min-h-[60vh] place-items-center px-6">
    <div className="text-center">
      <CircleNotch size={26} className="mx-auto animate-spin text-foreground/70" />
      <p className="mt-4 font-serif text-xl">Mapping the problem space…</p>
      <p className="mt-2 text-sm text-muted-foreground">Grouping the challenge into its problem categories.</p>
      <ThinkingTimer className="mt-5 text-center" />
    </div>
  </div>
);

export default function AreasOfNeed({ sessionId, data, currentVersionId, processing, error, onChanged, onNext }) {
  const [focus, setFocus] = useState(null); // {kind:'cat'|'tension', id}
  const [building, setBuilding] = useState(false);
  const [versions, setVersions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [fs, setFs] = useState(false);
  const diagramRef = useRef(null);
  const openTension = (t, on) => {
    setFocus(on ? null : { kind: "tension", id: t.id });
    if (!on) setTimeout(() => diagramRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
  };

  useEffect(() => {
    if (!data?.areas?.length) return;
    api.getAreasVersions(sessionId).then(setVersions).catch(() => {});
  }, [sessionId, data?.version_id, data?.areas?.length]);

  const restore = async (versionId) => {
    try {
      await api.restoreAreasVersion(sessionId, versionId);
      toast.success("Problem space version restored");
      setShowHistory(false);
      await onChanged?.();
    } catch (e) {
      toast.error("Could not restore", { description: e?.response?.data?.detail || e.message });
    }
  };

  const build = async () => {
    setBuilding(true);
    try { await api.buildAreasOfNeed(sessionId); await onChanged?.(); }
    catch (e) { toast.error("Could not build the problem space", { description: e?.response?.data?.detail || e.message }); }
    finally { setBuilding(false); }
  };

  if (processing || building) return <Building />;

  if (!data?.areas?.length) {
    return (
      <div data-testid="build-areas-prompt" className="grid min-h-[55vh] place-items-center px-6 text-center">
        <div className="max-w-lg">
          <Target size={30} className="mx-auto mb-4 text-foreground/70" />
          <h2 className="font-serif text-3xl">Map the problem space</h2>
          <p className="mx-auto mt-3 max-w-prose text-[15px] leading-relaxed text-muted-foreground">
            Group the challenge into its problem categories and the tensions between them. Together, these <em>are</em> the problem.
          </p>
          {error && <p className="mt-3 text-sm" style={{ color: "hsl(var(--destructive))" }}>Previous attempt failed: {String(error)}</p>}
          <button data-testid="build-areas-btn" onClick={build} className="mt-7 inline-flex items-center gap-2 rounded-sm border border-primary bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/85">
            <Target size={16} weight="bold" /> Map the Problem Space
          </button>
        </div>
      </div>
    );
  }

  const areas = data.areas;
  const tensions = data.area_tensions || [];
  const catColor = {};
  areas.forEach((a, i) => { catColor[a.id] = CAT_COLORS[i % CAT_COLORS.length]; });
  const totalElements = areas.reduce((s, a) => s + (a.needs?.length || 0), 0);
  const nameOf = (id) => cap(areas.find((a) => a.id === id)?.name);

  const N = areas.length;
  const R = 38;
  const CY = 44;
  const pos = {};
  areas.forEach((a, i) => { const ang = -Math.PI / 2 + (2 * Math.PI * i) / N; pos[a.id] = { x: 50 + R * Math.cos(ang), y: CY + R * Math.sin(ang) }; });

  const tensionsForCat = (id) => tensions.filter((t) => t.area_a === id || t.area_b === id);
  const litCats = focus?.kind === "cat" ? new Set([focus.id, ...tensionsForCat(focus.id).flatMap((t) => [t.area_a, t.area_b])])
    : focus?.kind === "tension" ? (() => { const t = tensions.find((x) => x.id === focus.id); return t ? new Set([t.area_a, t.area_b]) : null; })() : null;
  const litTensions = focus?.kind === "cat" ? new Set(tensionsForCat(focus.id).map((t) => t.id))
    : focus?.kind === "tension" ? new Set([focus.id]) : null;

  const activeCat = focus?.kind === "cat" ? areas.find((a) => a.id === focus.id) : null;
  const activeTension = focus?.kind === "tension" ? tensions.find((t) => t.id === focus.id) : null;

  const focusPos = activeCat
    ? pos[activeCat.id]
    : activeTension
    ? (() => { const a = pos[activeTension.area_a], b = pos[activeTension.area_b]; return a && b ? { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } : { x: 50, y: CY }; })()
    : null;
  const panelStyle = (fp) => {
    const openRight = fp.x <= 50;
    return {
      top: `${Math.min(Math.max(fp.y - 8, 2), 58)}%`,
      left: openRight ? `${fp.x}%` : "auto",
      right: openRight ? "auto" : `${100 - fp.x}%`,
      marginLeft: openRight ? 92 : 0,
      marginRight: openRight ? 0 : 92,
    };
  };

  return (
    <div data-testid="areas-of-need" className="w-full px-8 pb-28 pt-6">
      {data.built_from_version && currentVersionId && data.built_from_version !== currentVersionId && (
        <div data-testid="areas-stale-banner" className="mb-5 flex flex-col gap-2 rounded-sm border border-amber-500/50 bg-amber-500/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm leading-relaxed text-foreground/90">
            You've updated the analysis since this problem space was built. Rebuild it to fold in your latest answers.
          </p>
          <button data-testid="rebuild-areas-stale-btn" onClick={build} className="inline-flex shrink-0 items-center gap-1.5 rounded-sm border border-primary bg-primary px-3.5 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/85">
            <ArrowClockwise size={13} weight="bold" /> Rebuild problem space
          </button>
        </div>
      )}
      {/* Analysis of the Problem — shaded band above the diagram */}
      <div data-testid="areas-explanation" className="mb-6 w-full rounded-lg border border-border bg-muted/50 p-6">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-serif text-2xl">Analysis of the Problem</h2>
          <div className="flex items-center gap-3">
            {versions.length > 0 && (
              <button data-testid="areas-history-btn" onClick={() => setShowHistory((s) => !s)} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ClockCounterClockwise size={12} /> History ({versions.length})</button>
            )}
            <button data-testid="rebuild-areas-btn" onClick={build} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ArrowClockwise size={12} /> Rebuild</button>
          </div>
        </div>
        {showHistory && versions.length > 0 && (
          <div data-testid="areas-history-panel" className="mt-3 flex flex-wrap gap-2 border-t border-border pt-3">
            {versions.map((v) => (
              <button
                key={v.id}
                data-testid={`areas-version-${v.version_number}`}
                onClick={() => (v.is_current ? null : restore(v.id))}
                className={`inline-flex items-center gap-2 rounded-sm border px-3 py-1.5 text-xs transition-colors ${v.is_current ? "border-foreground" : "border-border hover:bg-muted"}`}
              >
                <span className="font-mono">v{v.version_number}</span>
                {v.is_current && <Check size={12} weight="bold" style={{ color: "hsl(var(--tag-preference))" }} />}
              </button>
            ))}
            <span className="self-center text-xs text-muted-foreground">Every rebuild is kept — click a version to restore it.</span>
          </div>
        )}
        <p className="mt-2 font-serif text-[17px] leading-relaxed text-foreground/90">
          This challenge isn't one problem — it's a <strong>system of {N} interlocking problem categories</strong> ({totalElements} problems in all).
          The tensions between them (the lines) are why solving any one in isolation tends to backfire.
        </p>
        {data.interpretation && <p className="mt-2 font-serif text-[17px] leading-relaxed text-foreground/90">{data.interpretation}</p>}
      </div>

      {/* problem-space diagram — inline or full-screen */}
      <div ref={diagramRef} data-testid="areas-diagram" className={fs ? "fixed inset-0 z-[60] flex flex-col bg-background" : "relative mx-auto w-full max-w-[600px] scroll-mt-24"}>
        {!fs && (
          <button data-testid="expand-problem-map-btn" onClick={() => setFs(true)} className="absolute right-1 top-1 z-10 inline-flex items-center gap-1.5 rounded-sm border border-border bg-card/90 px-3 py-1.5 text-xs font-medium shadow-sm backdrop-blur transition-colors hover:bg-muted"><ArrowsOut size={13} /> Full screen</button>
        )}
        {fs && (
          <div className="flex items-center justify-between border-b border-border px-5 py-2.5">
            <span className="font-serif text-lg">The Problem Space</span>
            <button data-testid="close-problem-fullscreen-btn" onClick={() => setFs(false)} className="inline-flex items-center gap-1.5 rounded-sm border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"><ArrowsIn size={13} /> Close</button>
          </div>
        )}
        <div className={fs ? "grid flex-1 place-items-center p-4" : ""}>
          <div className="relative aspect-square w-full" style={fs ? { width: "min(86vh,86vw)" } : { marginBottom: "-10%" }}>
        <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" style={{ overflow: "visible" }}>
          <circle cx="50" cy={CY} r={R} fill="none" stroke="hsl(var(--border))" strokeWidth="0.3" />
          {tensions.map((t) => {
            const a = pos[t.area_a], b = pos[t.area_b];
            if (!a || !b) return null;
            const on = !litTensions || litTensions.has(t.id);
            const meta = T[t.type] || T.trade_off;
            return (
              <g key={t.id} style={{ cursor: "pointer" }} onClick={() => setFocus(focus?.id === t.id ? null : { kind: "tension", id: t.id })}>
                <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="transparent" strokeWidth={3} />
                <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={meta.color} strokeWidth={on ? 0.8 : 0.4} strokeDasharray={t.type === "sequencing" ? "1.6 1.2" : undefined} opacity={on ? 0.9 : 0.14} />
              </g>
            );
          })}
        </svg>

        <div className="absolute left-1/2 top-[44%] -translate-x-1/2 -translate-y-1/2 text-center">
          <div className="rounded-full border border-border bg-card px-4 py-3 shadow-sm">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Problem space</div>
            <div className="text-xs font-medium">{N} categories · {tensions.length} tensions</div>
          </div>
        </div>

        {areas.map((a) => {
          const p = pos[a.id];
          const on = !litCats || litCats.has(a.id);
          const isFocus = focus?.kind === "cat" && focus.id === a.id;
          return (
            <button key={a.id} data-testid={`area-node-${a.id}`} onClick={() => setFocus(isFocus ? null : { kind: "cat", id: a.id })}
              className={`absolute w-[156px] -translate-x-1/2 -translate-y-1/2 transition-opacity ${on ? "opacity-100" : "opacity-30"}`}
              style={{ left: `${p.x}%`, top: `${p.y}%` }}>
              <div className={`border-l-[3px] bg-card p-2.5 text-left shadow-sm transition-colors ${isFocus ? "border border-foreground shadow-md" : "hover:bg-muted"}`} style={{ borderLeftColor: catColor[a.id] }}>
                <div className="text-[13px] font-medium leading-snug">{cap(a.name)}</div>
                <div className="mt-1 text-[10px] text-muted-foreground">{a.needs.length} problems</div>
              </div>
            </button>
          );
        })}

        {/* floating detail popup — hovers near the clicked node */}
        {focusPos && (
          <div data-testid="areas-detail-popup" className="absolute z-40 max-h-[82%] w-[290px] overflow-y-auto border border-border bg-card p-4 shadow-xl" style={panelStyle(focusPos)}>
            {activeCat ? (
              <div data-testid="area-detail">
                <div className="mb-1 flex items-start justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-medium" style={{ color: catColor[activeCat.id] }}><span className="h-2 w-2 rounded-full" style={{ background: catColor[activeCat.id] }} /> Problem category</span>
                  <button data-testid="close-detail" onClick={() => setFocus(null)} className="text-muted-foreground hover:text-foreground"><X size={15} /></button>
                </div>
                <h3 className="font-serif text-lg leading-tight">{cap(activeCat.name)}</h3>
                {activeCat.summary && <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{activeCat.summary}</p>}
                <div className="mt-3 space-y-2.5">
                  {activeCat.needs.map((n) => (
                    <div key={n.id} data-testid={`need-${n.id}`} className="border-l-2 pl-2.5" style={{ borderColor: catColor[activeCat.id] }}>
                      <p className="text-[12px] font-medium leading-snug">{n.short_name}</p>
                      <p className="mt-0.5 text-[12px] leading-relaxed text-muted-foreground">{n.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : activeTension ? (() => { const m = T[activeTension.type] || T.trade_off; const na = nameOf(activeTension.area_a); const nb = nameOf(activeTension.area_b); return (
              <div data-testid="tension-detail">
                <div className="mb-2.5 flex items-start justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-foreground"><m.Icon size={12} weight="bold" style={{ color: m.color }} /> Ongoing Tension <span className="font-normal text-muted-foreground">· {m.label}</span></span>
                  <button data-testid="close-detail" onClick={() => setFocus(null)} className="text-muted-foreground hover:text-foreground"><X size={15} /></button>
                </div>
                <div className="flex flex-wrap items-center text-[14px] font-semibold leading-snug text-foreground" style={{ color: m.color }}>
                  <span className="text-foreground">{cap(na)}</span><TensionBar /><span className="text-foreground">{cap(nb)}</span>
                </div>
                <p className="mt-2.5 text-[13px] font-medium leading-snug text-foreground">Your {na.toLowerCase()} is in tension with the problem of {nb.toLowerCase()}.</p>
                {(activeTension.plain || activeTension.why || activeTension.statement) && (
                  <p className="mt-1.5 text-[12.5px] leading-relaxed text-muted-foreground">{activeTension.plain || activeTension.why || activeTension.statement}</p>
                )}
              </div>
            ); })() : null}
          </div>
        )}
          </div>
        </div>
      </div>

      {/* how to use — flush left, directly below the diagram */}
      <p data-testid="areas-howto" className="mt-3 w-full font-serif text-[15px] leading-relaxed text-foreground/80">
        How to use this map: each node is a problem category; the lines are the tensions between them.
        Click a category to see the problems inside it, or click a tension line (or a chip below) to see the nature of that tension.
      </p>

      {/* reset control */}
      {focus && (
        <div className="mt-3 flex w-full">
          <button data-testid="reset-diagram" onClick={() => setFocus(null)} className="inline-flex items-center gap-1.5 rounded-sm border border-border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted">
            <ArrowsIn size={13} /> Show full diagram
          </button>
        </div>
      )}

      {/* tension links — shaded band below the diagram */}
      {tensions.length > 0 && (
        <div className="mt-6 w-full rounded-lg border border-border bg-muted/50 p-6">
          <h3 data-testid="explore-relations-heading" className="mb-3 font-serif text-2xl">Explore the Relations Between These Problems</h3>
          <p className="mb-3 w-full text-[13.5px] leading-relaxed text-foreground/80">Complex problems tend to be organized in a system — the problems are not independent of each other. As a result, it's usually not advisable to solve each problem in isolation from the others. That's why it's important to solve problems <em>in relation to</em> one another — to find strategies that resolve several at once. Click any tension below to explore the tensions between the problems you're facing.</p>
          <div className="flex flex-wrap justify-center gap-2">
            {tensions.map((t) => { const meta = T[t.type] || T.trade_off; const on = focus?.kind === "tension" && focus.id === t.id; return (
              <button key={t.id} data-testid={`tension-chip-${t.id}`} onClick={() => openTension(t, on)}
                className={`inline-flex items-center gap-1.5 rounded-sm border bg-card px-2.5 py-1 text-xs transition-colors ${on ? "border-foreground" : "border-border hover:border-foreground"}`}>
                <meta.Icon size={12} weight="bold" style={{ color: meta.color }} /> {cap(nameOf(t.area_a))} <TensionBar className="opacity-60" /> {cap(nameOf(t.area_b))}
              </button>
            ); })}
          </div>
        </div>
      )}

      {/* Next step — guide the user to Solutions */}
      <div data-testid="areas-next-cta" className="mt-8 w-full border-t border-border pt-6">
        <div className="flex flex-col gap-3 rounded-sm border border-primary/30 bg-primary/5 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-serif text-lg leading-tight">Done exploring the problem space?</p>
            <p className="mt-0.5 text-sm text-muted-foreground">Next, compose solution systems — approaches engineered to resolve several of these problems at once.</p>
          </div>
          <button
            data-testid="areas-next-solutions-btn"
            onClick={onNext}
            className="inline-flex shrink-0 items-center gap-2 rounded-sm border border-primary bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/85"
          >
            Next: Compose Solutions <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
